/**
 * The drums of a percussion part, read back out of a file.
 *
 * MusicXML keeps a drum part in two halves that are written a long way apart. `<part-list>`
 * says what the part can play -- one `<score-instrument>` per drum, each with the General
 * MIDI key it sounds -- and each `<note>` says which of those it is and where on the staff
 * it is drawn. Neither half is a kit on its own, so the reading collects both and puts them
 * together once the part has been read: the list gives the names and the sounds, the notes
 * give the places and the heads.
 *
 * A drum the part lists but never plays is left out of the kit. The file says nothing about
 * where it would be written, and inventing a line for it would be inventing notation.
 */

import type { Step } from "../document/pitch";
import type { DrumNote, NoteHead, PercussionKit } from "../document/percussion";
import { attr, child, childrenNamed, numberOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";

/** What `<part-list>` says a drum part can play, by the file's id for each drum. */
export type DrumListing = ReadonlyMap<string, { readonly name: string; readonly midi: number }>;

/** Where a drum turned out to be drawn, collected from the notes as they are read. */
export interface DrumSighting {
  readonly step: Step;
  readonly octave: number;
  readonly notehead: NoteHead;
  /** Whether the note carried the circle that means the drum is left to ring. */
  readonly open: boolean;
}

const HEADS: readonly NoteHead[] = ["normal", "x", "circle-x", "diamond", "triangle", "slash"];

/** The note head a drum is drawn with, or a plain one for a name this package does not use. */
export function headNamed(name: string | null): NoteHead {
  const trimmed = name?.trim() ?? "";
  return HEADS.find((head) => head === trimmed) ?? "normal";
}

/** The drums one `<score-part>` lists, empty when the part is a pitched one. */
export function drumsOf(element: XmlElement): DrumListing {
  const named = new Map<string, string>();
  for (const score of childrenNamed(element, "score-instrument")) {
    const id = attr(score, "id");
    if (id !== null) named.set(id, textOf(score, "instrument-name")?.trim() || id);
  }

  const drums = new Map<string, { name: string; midi: number }>();
  for (const midi of childrenNamed(element, "midi-instrument")) {
    const id = attr(midi, "id");
    const key = numberOf(midi, "midi-unpitched", 0);
    // MusicXML counts the unpitched keys from one; General MIDI counts them from zero.
    if (id === null || key < 1) continue;
    drums.set(id, { name: named.get(id) ?? id, midi: key - 1 });
  }
  return drums;
}

/** The drum a note names, or `null` when it names none this part lists. */
export function drumNameOf(note: XmlElement, listing: DrumListing): string | null {
  if (listing.size === 0) return null;
  const id = attr(child(note, "instrument"), "id");
  if (id !== null) return listing.get(id)?.name ?? null;
  // A part with one drum in it need not say so on every note.
  return listing.size === 1 ? [...listing.values()][0].name : null;
}

/** The kit a part turned out to use, or `null` when it played no drums at all. */
export function kitFrom(
  name: string,
  listing: DrumListing,
  seen: ReadonlyMap<string, DrumSighting>,
): PercussionKit | null {
  const notes: DrumNote[] = [];
  for (const drum of listing.values()) {
    const place = seen.get(drum.name);
    if (place === undefined) continue;
    notes.push({
      name: drum.name,
      midi: drum.midi,
      step: place.step,
      octave: place.octave,
      notehead: place.notehead,
      open: place.open,
      stem: "auto",
    });
  }
  return notes.length === 0 ? null : { name, notes };
}
